import React from "react";
import { Box, IconButton, Link, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { labelForLocation } from "../../helper/address";
import { MOBILE_PAGE_PAD_X, MOBILE_TOUCH_MIN_PX, MOBILE_TYPE } from "../../theme/mobileLayout";
import CompanyDetailTabsV2 from "./v2/CompanyDetailTabsV2";
import CompanyLinksRow from "./v2/CompanyLinksRow";
import CompanyDetailsActions from "./v2/CompanyDetailsActions";
import ClampedDescription from "./v2/ClampedDescription";
import { CompanyTagsRow } from "./v2/SummaryMetricCard";
import { HEADER } from "./v2/headerTokens";
import { SIDEBAR } from "./v2/sidebarVisual";

function getTopClassificationLabel(companyDetail) {
  const modeled = companyDetail?.modeledClassifications?.[0]?.levels;
  const sourced = companyDetail?.sourcedClassifications?.[0]?.levels;
  const levels = modeled || sourced;
  if (!levels) return null;

  const preferredKeys = [6, 5, 4, 3, 2, 1];
  for (const key of preferredKeys) {
    const description = levels[key]?.description;
    if (description) return description;
  }

  const first = Object.values(levels).find((level) => level?.description);
  return first?.description || null;
}

function collectTags(companyDetail, classificationLabel) {
  const tags = [];
  const seen = new Set();

  const push = (value) => {
    const label = String(value || "").trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    tags.push(label);
  };

  push(classificationLabel);

  (companyDetail?.keywords || []).forEach(push);

  const sourcedLevels = companyDetail?.sourcedClassifications?.[0]?.levels;
  if (sourcedLevels) {
    [4, 5, 3].forEach((key) => push(sourcedLevels[key]?.description));
  }

  return tags.slice(0, 10);
}

function websiteHref(website) {
  if (!website) return undefined;
  return website.includes("http") ? website : `https://${website}`;
}

function websiteLabel(website) {
  if (!website) return "";
  return String(website).replace(/^https?:\/\//i, "").replace(/\/$/, "");
}

function CompanyModalV2Content({
  companyDetail,
  imageData,
  onClose,
  compact = false,
  descriptionExpanded = false,
  onToggleDescription,
  stickyHeader = false,
  activeTab = 0,
  onTabChange,
  enrichmentHistory = [],
  enrichmentHistoryError,
  enrichmentHistoryLoading = false,
  onActionStart,
  onActionResult,
  actionsRef,
  draftSessionId,
  onDraftSessionUsed,
  onRunEnrichmentAction,
}) {
  const locationLabel = labelForLocation(companyDetail?.primaryLocation);
  const classificationLabel = getTopClassificationLabel(companyDetail);
  const overviewText = companyDetail?.descriptive?.[0];
  const tags = collectTags(companyDetail, classificationLabel);
  const primaryEmail = companyDetail?.emails?.[0];
  const website = companyDetail?.primaryWebsite;
  const href = websiteHref(website);
  const siteLabel = websiteLabel(website);

  if (compact) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: MOBILE_PAGE_PAD_X,
            pt: 0.5,
            pb: 1,
            borderBottom: stickyHeader ? "1px solid rgba(88, 89, 91, 0.1)" : "none",
            bgcolor: "background.paper",
          }}
        >
          {/* Row 1: Logo → name/category → Close */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: 1 }}>
              <Box
                component="img"
                src={imageData}
                alt=""
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "6px",
                  objectFit: "cover",
                  flexShrink: 0,
                  border: HEADER.border,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    ...MOBILE_TYPE.companyName,
                    color: "text.primary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {companyDetail?.primaryName}
                </Typography>
                {classificationLabel ? (
                  <Typography
                    sx={{
                      mt: 0.15,
                      ...MOBILE_TYPE.caption,
                      color: "text.secondary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {classificationLabel}
                  </Typography>
                ) : null}
              </Box>
            </Box>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                flexShrink: 0,
                mt: -0.5,
                mr: -1,
              }}
            >
              <CompanyDetailsActions
                ref={actionsRef}
                companyDetail={companyDetail}
                draftSessionId={draftSessionId}
                onDraftSessionUsed={onDraftSessionUsed}
                onRunEnrichmentAction={onRunEnrichmentAction}
                onActionStart={onActionStart}
                onActionResult={onActionResult}
              />
              <IconButton
                onClick={onClose}
                aria-label="Close company details"
                sx={{
                  minWidth: MOBILE_TOUCH_MIN_PX,
                  minHeight: MOBILE_TOUCH_MIN_PX,
                  color: "text.secondary",
                  flexShrink: 0,
                  "&:hover": { color: "primary.main" },
                }}
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Row 2: Website · Location (no socials in primary mobile header) */}
          {(href || locationLabel) ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 0.75,
                minWidth: 0,
                flexWrap: "nowrap",
                overflow: "hidden",
              }}
            >
              {href ? (
                <Link
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    ...MOBILE_TYPE.helper,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "text.secondary",
                    minWidth: 0,
                    maxWidth: "55%",
                  }}
                >
                  <LanguageOutlinedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {siteLabel}
                  </Box>
                </Link>
              ) : null}
              {href && locationLabel ? (
                <Typography component="span" sx={{ ...MOBILE_TYPE.caption, color: "text.disabled" }}>
                  ·
                </Typography>
              ) : null}
              {locationLabel ? (
                <Typography
                  sx={{
                    ...MOBILE_TYPE.helper,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "text.secondary",
                    minWidth: 0,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <PlaceOutlinedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                  {locationLabel}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            px: MOBILE_PAGE_PAD_X,
            pt: 0,
            pb: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          <CompanyDetailTabsV2
            companyDetail={companyDetail}
            compact
            activeTab={activeTab}
            onTabChange={onTabChange}
            enrichmentHistory={enrichmentHistory}
            enrichmentHistoryError={enrichmentHistoryError}
            enrichmentHistoryLoading={enrichmentHistoryLoading}
            onRequestOpenActions={() => actionsRef?.current?.openMenu?.()}
          />
        </Box>
      </Box>
    );
  }

  // Desktop identity header (unchanged structure)
  return (
    <Box
      sx={{
        px: SIDEBAR.pagePadX,
        pt: SIDEBAR.pagePadTop,
        pb: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: HEADER.gap.logoText,
          mb: HEADER.gap.identityToLinks,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: HEADER.gap.logoText,
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            component="img"
            src={imageData}
            alt=""
            sx={{
              width: HEADER.logoSize,
              height: HEADER.logoSize,
              borderRadius: "8px",
              objectFit: "cover",
              flexShrink: 0,
              border: HEADER.border,
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  ...HEADER.title,
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {companyDetail?.primaryName}
              </Typography>
              <CompanyLinksRow
                website={companyDetail?.primaryWebsite}
                email={primaryEmail}
                locationLabel={locationLabel}
                social={companyDetail?.social}
              />
            </Box>
            {classificationLabel ? (
              <Typography
                sx={{
                  ...HEADER.subtitle,
                  mt: 0.35,
                  color: "text.secondary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {classificationLabel}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            flexShrink: 0,
            gap: 0.75,
            mt: -0.75,
            mr: -0.75,
          }}
        >
          <CompanyDetailsActions
            ref={actionsRef}
            companyDetail={companyDetail}
            draftSessionId={draftSessionId}
            onDraftSessionUsed={onDraftSessionUsed}
            onRunEnrichmentAction={onRunEnrichmentAction}
            onActionStart={onActionStart}
            onActionResult={onActionResult}
          />
          <IconButton
            onClick={onClose}
            aria-label="Close company details"
            sx={{
              minWidth: MOBILE_TOUCH_MIN_PX,
              minHeight: MOBILE_TOUCH_MIN_PX,
              color: "text.secondary",
              flexShrink: 0,
              "&:hover": { color: "primary.main" },
            }}
          >
            <CloseIcon sx={{ fontSize: HEADER.iconSize }} />
          </IconButton>
        </Box>
      </Box>

      {overviewText ? (
        <Box sx={{ mb: HEADER.gap.descriptionToChips, width: "100%", minWidth: 0 }}>
          <ClampedDescription
            text={overviewText}
            expanded={descriptionExpanded}
            onToggle={onToggleDescription}
          />
        </Box>
      ) : null}

      {tags.length > 0 ? (
        <Box sx={{ mb: HEADER.gap.chipsToTabs }}>
          <CompanyTagsRow tags={tags} />
        </Box>
      ) : (
        <Box sx={{ mb: HEADER.gap.chipsToTabs }} />
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CompanyDetailTabsV2
          companyDetail={companyDetail}
          compact={false}
          activeTab={activeTab}
          onTabChange={onTabChange}
          enrichmentHistory={enrichmentHistory}
          enrichmentHistoryError={enrichmentHistoryError}
          enrichmentHistoryLoading={enrichmentHistoryLoading}
          onRequestOpenActions={() => actionsRef?.current?.openMenu?.()}
        />
      </Box>
    </Box>
  );
}

export { CompanyModalV2Content, getTopClassificationLabel, collectTags, websiteHref, websiteLabel };
